"use client";
import { ReactElement, useState, useMemo } from "react";
import { cn } from "@/lib/cn";

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function toYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

type Props = {
  availableDates: string[];
  selectedDate: string;
  onSelectDate: (date: string) => void;
};

export function BookingCalendar({ availableDates, selectedDate, onSelectDate }: Props): ReactElement {
  const availableSet = useMemo(() => new Set(availableDates), [availableDates]);

  const firstAvailable = availableDates[0];
  const initialDate = firstAvailable ? new Date(firstAvailable + "T12:00:00") : new Date();

  const [viewYear, setViewYear] = useState(initialDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialDate.getMonth()); // 0-indexed

  const monthName = new Date(viewYear, viewMonth, 1).toLocaleString("default", { month: "long", year: "numeric" });

  const firstDay = new Date(viewYear, viewMonth, 1).getDay(); // day of week 0-6
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells: Array<{ day: number | null; ymd: string | null }> = [];
  for (let i = 0; i < firstDay; i++) {
    cells.push({ day: null, ymd: null });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const month = String(viewMonth + 1).padStart(2, "0");
    const dayStr = String(d).padStart(2, "0");
    const ymd = `${viewYear}-${month}-${dayStr}`;
    cells.push({ day: d, ymd });
  }

  const handlePrev = (): void => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNext = (): void => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const today = toYMD(new Date());

  return (
    <div className="w-full">
      {/* Month navigation */}
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={handlePrev}
          className="rounded-full p-2 text-neutral-400 hover:bg-white hover:text-neutral-700 hover:shadow-sm transition-all"
          aria-label="Previous month"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span className="text-sm font-bold text-neutral-800">{monthName}</span>
        <button
          onClick={handleNext}
          className="rounded-full p-2 text-neutral-400 hover:bg-white hover:text-neutral-700 hover:shadow-sm transition-all"
          aria-label="Next month"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day labels */}
      <div className="mb-2 grid grid-cols-7">
        {DAYS.map((d) => (
          <div key={d} className="py-1 text-center text-xs font-semibold text-neutral-400">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          if (!cell.day || !cell.ymd) {
            return <div key={idx} />;
          }

          const isAvailable = availableSet.has(cell.ymd);
          const isSelected = cell.ymd === selectedDate;
          const isToday = cell.ymd === today;

          return (
            <div key={idx} className="flex items-center justify-center py-1">
              <button
                onClick={() => isAvailable && onSelectDate(cell.ymd!)}
                disabled={!isAvailable}
                className={cn(
                  "h-9 w-9 rounded-full text-sm transition-all",
                  isSelected
                    ? "bg-[#029391] text-white font-bold shadow-md"
                    : isAvailable
                    ? "font-medium text-neutral-800 hover:bg-white hover:text-[#029391] hover:shadow-sm"
                    : "cursor-not-allowed text-neutral-300",
                  isToday && !isSelected && "ring-2 ring-[#029391] ring-offset-1"
                )}
              >
                {cell.day}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
